/**
 * ******************************************************************************************************************************
 * Responsibility - Renders a drawer that allows users to pay for one or multiple charges. Its open/ close state is controlled by
 * the HOC parent component and does not provide a trigger to toggle the drawer. If you wish to render one pass it as a single
 * child component. In order for this component to work it must be wrapped in the use_charges_and_invoice HOC that
 * provides the necessary props to pay for single or multiple invoices/ charges.
 *
 * If using it to pay for a single charge, pass the charge_batch in the singleCharge prop and set isPaySingle to true.
 * ********************************************************************************************************************
 *
 * @format
 */

import {formatSingleUserProfiles, formatUserProfiles} from '../../../../cart/helpers/format';
import {Drawer, DrawerFooter, DrawerHeader} from '@sttark/design-system';
import AddEditForm from '../../payments/add_credit_profile_form';
import {ErrorDisplay, If, IfElse} from 'frontier-utilities';
import {usePayBalance} from '../use_pay_balance';
import NumberFormat from 'react-number-format';
import {Select} from '@sttark/design-system';
import React, {useState} from 'react';

// style
import './pay_charge.scss';

export const PayCharge = props => {
    const {
        onGetPaymentProfiles,
        payMultipleList = [],
        payment_profile_id,
        handleToggleModal,
        showChargesModal,
        handleRemoveItem,
        paymentProfiles,
        isPayingSingle,
        handleAddItem,
        charges = [],
        singleCharge,
        formData,
        onChange,
        loading,
        saving,
        error
    } = props;

    const [showAddPaymentMethodForm, setAddPaymentMethodForm] = useState(false);

    const payCharges = usePayBalance({
        isPayingSingle,
        payMultipleList,
        isChargeBatch: true,
        singleCharge
    });
    const {handlePayment, onChooseProfile, isProcessing, paymentError} = payCharges;

    // get the individual change to pay for if this a single payment
    const findSingleCharge = props.singleCharge
        ? props.singleCharge
        : charges?.find(({id}) => id === payMultipleList[0]) || {};
    const chargesToPayFor = isPayingSingle ? [findSingleCharge] : charges;

    // get the total to pay for
    let total;
    // if it is a single charge, use the total from the cart
    if (isPayingSingle) {
        total = findSingleCharge.total;
    } else if (charges?.length > 0) {
        total = charges.reduce((total, item) => {
            total += payMultipleList.indexOf(item.id) > -1 ? parseFloat(item.total) : 0;
            return total;
        }, 0);
    }

    const parsedProfiles = formatUserProfiles(paymentProfiles)?.profiles || [];
    const currentProfile = formatSingleUserProfiles(
        parsedProfiles.find(profile => profile.value === payment_profile_id)
    );

    const handleChange = profileId => {
        if (profileId === 'cta') {
            setAddPaymentMethodForm(true);
            return;
        }

        const profile = paymentProfiles.find(profile => profile.id === profileId);
        onChooseProfile(profile);
    };

    return (
        <Drawer onClose={() => handleToggleModal('charges')} isOpen={showChargesModal}>
            <DrawerHeader
                onCancel={() => handleToggleModal('charges')}
                title={isPayingSingle ? 'Pay A Charge' : 'Pay All Charges'}
            />
            <div>
                <IfElse condition={loading}>
                    <div>
                        Retrieving available payment profiles...
                        <i className="fa fa-spin fa-spinner" />{' '}
                    </div>
                    <div>
                        <div className={'select-payment-method--dropdown mb-2'}>
                            <Select
                                className={'select-payment-method--dropdown'}
                                placeholder="Select Payment method"
                                initialValue={currentProfile}
                                options={parsedProfiles}
                                onChange={handleChange}
                            />
                        </div>
                        <AddEditForm onSuccessfulSave={onGetPaymentProfiles}>
                            <button className="color-accent-primary m-0 p-0">
                                <i className="fa fa-plus" />
                                <u>Add payment method</u>
                            </button>
                        </AddEditForm>
                    </div>
                </IfElse>
                <div>
                    <h4>Included Charges</h4>
                    <table width="100%">
                        <thead className={'bg-structure-lightest'}>
                            <tr>
                                <th className="p-3">Invoice #</th>
                                <th className="p-3">Amount Due</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {chargesToPayFor.map((item, index) => {
                                return (
                                    <tr key={`${item.id}-${index}`}>
                                        <td className="p-3">{item.id}</td>
                                        <td className="p-3">
                                            <NumberFormat
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                value={item.total}
                                                displayType="text"
                                                decimalScale={2}
                                                prefix={'$'}
                                            />
                                        </td>
                                        <td className="p-3">
                                            <If condition={!isPayingSingle}>
                                                <IfElse
                                                    condition={
                                                        payMultipleList.indexOf(item.id) > -1
                                                    }>
                                                    <button
                                                        onClick={() => handleRemoveItem(item.id)}
                                                        className="color-alert-negative"
                                                        id={item.id}>
                                                        <i className="fa fa-xmark" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleAddItem(item.id)}
                                                        className="color-accent-primary"
                                                        id={item.id}>
                                                        <i className="fa fa-plus" />
                                                    </button>
                                                </IfElse>
                                            </If>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <IfElse condition={isProcessing}>
                <DrawerFooter>
                    <h4>Processing</h4>
                </DrawerFooter>
                <IfElse condition={!!paymentError}>
                    <DrawerFooter>
                        <ErrorDisplay className="alert alert-danger d-block" error={paymentError} />
                    </DrawerFooter>
                    <DrawerFooter
                        title={
                            <NumberFormat
                                decimalScale={2}
                                fixedDecimalScale={true}
                                displayType="text"
                                prefix="$"
                                value={total}
                            />
                        }
                        okTitle={'Pay Charges'}
                        onOk={handlePayment}
                    />
                </IfElse>
            </IfElse>
        </Drawer>
    );
};
